{
  "common": {
    "resmed-logo-alt": "Resmed logo"
  },
  "error-screen": {
    "title": "Something went wrong",
    "message": "We're sorry, but we weren't able to process your request. Please try again later."
  },
  "polling-screen": {
    "reviewing-your-answers": "Reviewing your answers",
    "assessing-sleep-data": "Summarizing sleep insights",
    "preparing-your-results": "Preparing your results",
    "loading-spinner": "Loading",
    "checkmark": "Complete"
  },
  "validation": {
    "required": "This field is required",
    "invalid": "Invalid input",
    "invalid-characters": "Contains invalid characters",
    "max-length-exceeded": "Maximum length exceeded",
    "name-required": "Name is required",
    "name-max-length": "Name must be 50 characters or less",
    "name-invalid-characters": "Name can only contain letters, spaces, hyphens, and apostrophes",
    "email-required": "Email is required",
    "email-max-length": "Email must be 100 characters or less",
    "email-invalid": "Please enter a valid email address",
    "email-soft-reject": "We could not verify this email address.",
    "phone-required": "Phone number is required",
    "phone-invalid": "Please enter a valid phone number",
    "number-invalid": "Please enter a valid number",
    "height-invalid": "Please enter a valid height",
    "height-ft-range": "Height must be between 1 and 9 feet",
    "height-in-range": "Please enter inches as 0-11 (values over 11 inches should use feet)",
    "height-cm-range": "Height must be between 50 and 275 cm",
    "weight-invalid": "Please enter a valid weight",
    "weight-lbs-range": "Weight must be between 50 and 700 lbs",
    "weight-kg-range": "Weight must be between 20 and 320 kg"
  },
  "questionnaire": {
    "back": "Back",
    "next": "Next",
    "skip": "Skip",
    "skip-step": "Skip step",
    "take-selfie": "Take selfie",
    "see-results": "See results",
    "loading": "Loading questionnaire...",
    "why-are-we-asking": "Why are we asking?",
    "body-text": {
      "select-all-that-apply": "Select all that apply.",
      "pick-most-applicable": "Pick most applicable."
    }
  },
  "selfie": {
    "selfie-intro": {
      "title": "What your face may say about your sleep",
      "card-title": "Take a selfie",
      "card-body": "Your facial features may offer clues about how you're breathing while you sleep. A quick selfie can help identify visible facial patterns that could be associated with obstructive sleep apnea (OSA).",
      "learn-more": "Learn more",
      "disclaimer": "<title>Disclaimer:</title> The Selfie Screener tool is for informational and educational purposes only. This tool is not clinically validated or is not a substitute for the professional judgement of a health care professional in diagnosing and treating patients. Always seek the advice of a qualified health provider with any questions you may have regarding a medical condition.",
      "image-alt-male": "Male face with facial detection overlay",
      "image-alt-female": "Female face with facial detection overlay"
    },
    "selfie-info-popup": {
      "title": "Why face anatomy matters",
      "body": "The relationships between your facial structure, jaw alignment and neck can reveal how your airway behaves during sleep. A brief facial analysis can assess these subtle features to help you understand your sleep and breathing patterns.<sup>1</sup>",
      "cta": "Take a selfie",
      "faq-title": "FAQs",
      "image-alt": "Female face with facial analysis overlays",
      "faq": {
        "how-does-selfie-screener-work": {
          "question": "How does the Selfie Screener work?",
          "answer": "The Selfie Screener analyzes your selfie to identify visual features - such as facial structure, proportions and other subtle features - that may be associated with sleep apnea. The tool offers informational insights only to help you decide whether to explore your sleep health further. It does not diagnose or assess medical risk."
        },
        "is-selfie-screener-based-on-clinical-research": {
          "question": "Is the Selfie Screener based on clinical research?",
          "answer": "The Selfie Screener was informed by existing research and model training data to explore visual patterns that may relate to sleep-disordered breathing. It is intended for education and awareness and is not a clinical diagnosis tool."
        },
        "how-was-selfie-screener-developed": {
          "question": "How was Selfie Screener developed?",
          "answer": "The Selfie Screener was designed using an AI-powered model trained on selfies from thousands of volunteers. The goal was to explore whether certain visual features could be associated with sleep apnea and to offer a quick, engaging way for people to learn more about their sleep health."
        },
        "do-i-need-any-special-equipment": {
          "question": "Do I need any special equipment?",
          "answer": "No special equipment is needed. You only need a smartphone or camera-enabled device with good lighting so the selfie can be captured clearly."
        },
        "is-my-selfie-or-data-stored": {
          "question": "Is my selfie or data stored?",
          "answer": "Your selfie is processed securely for the assessment experience. Please review the privacy information provided in your assessment flow for details on data handling and retention."
        },
        "how-accurate-is-the-selfie-screener-tool": {
          "question": "How accurate is the Selfie Screener tool?",
          "answer": "The Selfie Screener provides informational insights and should not be used as a diagnosis. Accuracy can vary based on image quality and other factors, and any medical concerns should be discussed with a qualified healthcare professional."
        },
        "what-happens-after-i-finish": {
          "question": "What happens after I finish?",
          "answer": "After you complete the selfie step, you will return to the assessment flow and can continue to your personalized results and next-step guidance."
        }
      },
      "references-title": "References",
      "references": {
        "ref-1": "Source: <a>https://pmc.ncbi.nlm.nih.gov/articles/PMC2938867/</a>"
      }
    },
    "selfie-capture": {
      "title": "Selfie Screen — placeholder",
      "align-face": "Your head should be centered and fill the entire frame.",
      "capture-complete": "Capture completed!",
      "perfect-position": "Perfect position, hold still!",
      "re-align": "Your head should be centered and fill the entire frame.",
      "scanning-facial-patterns": "Scanning facial patterns.",
      "amazingly-done": "Amazingly done!",
      "upload-error": "Something went wrong uploading your photo. Please try again."
    },
    "prescan": {
      "instructions": {
        "titleLine1": "Let's set you up for your best scan",
        "titleLine2": "",
        "well-lit": "Make sure your face is well lit",
        "centered": "Center your face",
        "soundOn": "To hear instructions, take your phone off silent",
        "startScanning": "Start scanning"
      }
    },
    "camera-denied-banner": "We couldn't access your camera. Please try again, or skip this step to continue and view your sleep assessment results.",
    "camera-denied": "We couldn't access your camera. Please try again.",
    "try-again": "Try again",
    "skip-selfie": "Skip selfie, take assessment"
  },
  "aria": {
    "toggle-information": "Toggle information",
    "unit-system": "Unit system",
    "navigation-label": "Questionnaire navigation",
    "close": "Close",
    "dismiss-error": "Dismiss error",
    "progress-bar": "Questionnaire progress",
    "previous-card": "Previous card",
    "next-card": "Next card",
    "card-indicators": "Card navigation",
    "symptom-carousel": "Your symptom results",
    "go-back": "Go back",
    "treatment-card": "Treatment card for {treatment}",
    "learn-more-about": "Learn more about {treatment}",
    "close-treatment-details": "Close treatment details",
    "talk-to-clinician-about": "Talk to a clinician about {treatment}",
    "treatment-carousel": "Treatment options carousel",
    "play-carousel": "Play auto-scroll",
    "pause-carousel": "Pause auto-scroll"
  },
  "units": {
    "height": {
      "ft": "ft",
      "cm": "cm"
    },
    "weight": {
      "lbs": "lbs",
      "kg": "kg"
    }
  },
  "transitions": {
    "talk-about-sleep": {
      "title": "Let's talk about your sleep.",
      "body": "This will include questions that can help us better understand your sleep habits and any signs of poor sleep you might be experiencing.",
      "female": {
        "title": "Let's talk about your sleep.",
        "body": "This will include questions that can help us better understand your sleep habits and any signs of poor sleep you might be experiencing."
      }
    },
    "sleep-quality-stats": {
      "overline": "Did you know?",
      "title": "Across the world, most people report only getting four good nights of sleep each week.",
      "body": "",
      "image-alt": "A tired man rubbing his eyes",
      "source": "Source: Resmed. (2025). Global sleep survey. <sourceLink>https://sleepsurvey.resmed.com/</sourceLink>.",
      "female": {
        "title": "While most adults need seven or more hours of sleep each night, women are more likely than men to report trouble falling asleep or staying asleep.",
        "source": "Source: Adjaye-Gbewonyo, D., Ng, A. E., & Black, L. I. (2022). Sleep difficulties in adults: United States, 2020.",
        "image-alt": "A woman yawning on a bus."
      }
    },
    "productivity-impact": {
      "overline": "Did you know?",
      "title": "Lack of quality sleep may impact productivity.",
      "body-1": "According to the Resmed 2025 Global Sleep Survey, <bold>71%</bold> of employed respondents report calling in sick due to poor sleep.",
      "body-2": "And after a restless or disrupted night, <bold>31%</bold> of people report struggling to concentrate the next day.",
      "image-alt": "A woman looking at a computer screen",
      "source": "Source: Resmed. (2025). Global sleep survey. <sourceLink>https://sleepsurvey.resmed.com/</sourceLink>",
      "female": {
        "title": "Lack of quality sleep may impact productivity.",
        "body-1": "According to the Resmed 2025 Global Sleep Survey, <bold>71%</bold> of employed respondents report calling in sick due to poor sleep.",
        "body-2": "And after a restless or disrupted night, <bold>31%</bold> of people report struggling to concentrate the next day.",
        "image-alt": "A woman looking at a computer screen",
        "source": "Source: Resmed. (2025). Global sleep survey. <sourceLink>https://sleepsurvey.resmed.com/</sourceLink>"
      }
    }
  },
  "questions": {
    "birth-year": {
      "question": "What year were you born?",
      "select-year": "Select year",
      "info": "As we age, the risk of sleep apnea may increase. Excess weight is one of several factors associated with sleep apnea.",
      "info-source": "Source: Slowik, J. M., Sankari, A., & Collen, J. F. (2025). Obstructive sleep apnea. In StatPearls [Internet]. StatPearls Publishing.",
      "female": {
        "info": "As women get older, changes like menopause can affect sleep. In a global survey, 44% of menopausal women reported trouble falling asleep three or more nights a week.",
        "info-source": "Source: Resmed's 2025 Global Sleep Survey. <a>https://sleepsurvey.resmed.com/</a>"
      },
      "answers": {
        "label": "Birth year"
      }
    },
    "email": {
      "question": "What is your email?",
      "answers": {
        "email": {
          "label": "Email address"
        }
      }
    },
    "height": {
      "question": "What is your height?",
      "answers": {
        "feet": {
          "label": "Feet"
        },
        "inches": {
          "label": "Inches"
        },
        "centimeters": {
          "label": "Centimeters"
        }
      }
    },
    "high-blood-pressure": {
      "question": "Do you have high blood pressure or are you taking medication to treat it?",
      "answers": {
        "yes": "Yes",
        "no": "No",
        "prefer-not-to-say": "Prefer not to answer"
      }
    },
    "hold-breath-sleeping": {
      "question": "Have you ever been told you hold your breath while sleeping?",
      "answers": {
        "yes": "Yes",
        "no": "No"
      }
    },
    "how-audible-snore": {
      "question": "How audible is your snoring (with the door shut)?",
      "answers": {
        "barely-audible": "Barely audible",
        "can-be-heard-in-same-room": "Can be heard in the same room",
        "can-be-heard-in-next-room": "Can be heard in the next room",
        "can-be-heard-down-the-hall": "Can be heard down the hall",
        "dont-know": "Don't know"
      }
    },
    "how-many-hours-sleep": {
      "question": "On average, how many hours of sleep do you get each night?",
      "answers": {
        "less-than-five": "Less than 5 hours",
        "five-to-seven": "5-7 hours",
        "seven-to-nine": "7-9 hours",
        "more-than-nine": "9+ hours"
      }
    },
    "how-often-nap": {
      "question": "How often do you nap during the day?",
      "answers": {
        "never": "Never",
        "rarely": "Rarely (once a month)",
        "occasionally": "Occasionally (a few times a month)",
        "often": "Often (a few times a week)",
        "always": "Always (everyday)"
      }
    },
    "phone": {
      "question": "What is your mobile number?",
      "answers": {
        "phone": {
          "label": "Mobile number (Optional)"
        }
      }
    },
    "restless-legs": {
      "question": "Do you sometimes feel that you have to move your legs to make them feel comfortable?",
      "answers": {
        "yes": "Yes",
        "no": "No"
      }
    },
    "user-gender": {
      "question": "What is your gender?",
      "answers": {
        "male": "Man",
        "female": "Woman",
        "non-binary": "Non-binary",
        "prefer-not-to-mention": "Prefer not to say",
        "other": "Other"
      }
    },
    "sex-at-birth": {
      "question": "What sex were you assigned at birth?",
      "info": "Symptoms of sleep conditions may present differently in men and women. Many screening tools focus on symptoms more commonly associated with men, like snoring.<sup>1</sup> Knowing the sex you were assigned at birth can help us ask questions that may better reflect your experience.",
      "info-source": "Source: <sup>1</sup>Bauters et al. Sleep Med. 2020 Mar;67:91-98.",
      "answers": {
        "male": "Male",
        "female": "Female",
        "prefer-not-to-mention": "Prefer not to say"
      }
    },

    "sleep-difficulties": {
      "question": "During your sleep, which of the following applies to you?",
      "answers": {
        "difficulty-falling-asleep": "I have difficulty falling asleep",
        "difficulty-staying-asleep": "I have difficulty staying asleep",
        "wake-up-during-night": "I often wake up during the night",
        "wake-up-earlier": "I wake up earlier than I would like",
        "none": "None of the above"
      }
    },
    "sleep-motivation": {
      "question": "What has been your key motivation to learn more about your sleep?",
      "answers": {
        "partner-snoring": "Partner wants me to stop snoring",
        "low-energy-fatigue": "Low energy and fatigue",
        "ongoing-health-issues": "Ongoing health issues or risk",
        "doctors-recommendation": "Doctor's recommendation",
        "work-productivity": "Work productivity & performance issues",
        "sudden-event": "Sudden event",
        "other": "Any other"
      }
    },
    "cpap-therapy-status": {
      "question": "Please tell us about your current status.",
      "answers": {
        "diagnosed-but-never-started": "I am diagnosed with OSA, but I have never started CPAP therapy.",
        "started-but-no-longer-use": "I have started CPAP therapy in the past, but I no longer use it.",
        "currently-using": "I am currently using CPAP therapy.",
        "none-of-the-above": "None of the above"
      }
    },
    "sleep-or-mental-health-conditions": {
      "question": "Have you ever been diagnosed with, or do you currently experience, any of the following conditions?",
      "answers": {
        "sleep-apnea": "Sleep Apnea",
        "insomnia": "Insomnia",
        "restless-leg-syndrome": "Restless Legs Syndrome",
        "fragmented-sleep": "Fragmented Sleep",
        "bruxism": "Bruxism",
        "circadian-rhythm-disorder": "Circadian Rhythm Disorder",
        "depression": "Depression",
        "anxiety": "Anxiety",
        "other": "Other",
        "prefer-not-to-say": "Prefer not to say",
        "none-of-the-above": "None of the above"
      }
    },
    "sleep-quality": {
      "question": "How would you describe your sleep?",
      "body": "Pick most applicable.",
      "answers": {
        "great": "Great",
        "deep": "Deep",
        "light": "Light",
        "could-be-better": "Could be better",
        "disturbed": "Disturbed",
        "terrible": "Terrible"
      }
    },
    "submit": {
      "question": "You're all done.",
      "body": "Now let's take a look at your personalized results to better understand how your sleep may be related to how you feel. Your results may help you consider what steps to take next.",
      "disclaimer": "<bold>Disclaimer:</bold> These results are informational only and are not intended to diagnose or treat any medical condition. For medical advice, please consult your healthcare provider."
    },
    "told-you-snore": {
      "question": "Have you been told you snore?",
      "answers": {
        "no": "No",
        "yes": "Yes"
      }
    },
    "user-name": {
      "question": "What is your name?",
      "answers": {
        "first-name": {
          "label": "First name"
        },
        "last-name": {
          "label": "Last name"
        }
      }
    },
    "wake-up-with-symptoms": {
      "question": "Do you wake up with any of the following symptoms?",
      "answers": {
        "headaches": "Headaches",
        "dry-mouth": "Dry mouth",
        "aching-jaw": "Aching jaw (or you have been told you grind your teeth)",
        "none-of-the-above": "None of the above"
      }
    },
    "trouble-sleeping-pain": {
      "question": "How often do you have trouble sleeping because of pain?",
      "answers": {
        "never": "Never",
        "less-than-once-a-week": "Less than once a week",
        "one-to-two-times-a-week": "1-2 times a week",
        "three-or-more-times-a-week": "3 or more times a week"
      }
    },
    "wake-gasping-for-breath": {
      "question": "Do you ever wake up gasping for breath?",
      "answers": {
        "yes": "Yes",
        "no": "No"
      }
    },
    "daytime-fatigue": {
      "question": "Do you regularly experience any of the following, even after a full night's sleep?",
      "answers": {
        "low-energy": "Low energy",
        "feeling-tired": "Feeling tired",
        "daytime-sleepiness": "Daytime sleepiness",
        "waking-up-unrested": "Waking up feeling unrested",
        "none-of-the-above": "None of the above"
      }
    },
    "menopausal-status": {
      "question": "Which category best describes you?",
      "answers": {
        "pre-menopausal": "Pre-menopausal",
        "pre-menopausal-description": "Still having regular menstrual periods or continued ovulation with/without birth control",
        "peri-menopausal": "Peri-menopausal",
        "peri-menopausal-description": "Menstrual periods have irregular intervals and cycle lengths vary; menstrual periods have become heavier or lighter; have skipped/missed periods; or recently changed",
        "post-menopausal-surgery": "Post-menopausal due to surgery",
        "post-menopausal-surgery-description": "E.g.; bilateral oophorectomy or removal of both ovaries; total hysterectomy with bilateral oophorectomy",
        "post-menopausal": "Post-menopausal",
        "post-menopausal-description": "No menstrual periods for 12 months or more that is not due to other medical causes",
        "not-sure": "Not sure"
      }
    },

    "weight": {
      "question": "What is your weight?",
      "answers": {
        "weight-lbs": {
          "label": "Pounds"
        },
        "weight-kg": {
          "label": "Kilograms"
        }
      }
    }
  },
  "results-page": {
    "sleep-clinic-section": {
      "title-default": "Find a sleep clinic",
      "body-default": "Contact your doctor for a referral, or visit our sleep clinic locator to find a sleep specialist in a location that's convenient for you.",
      "button-default": "Find a clinic",
      "image-alt-default": "A smiling male doctor with a stethoscope",
      "title-female": "Find a sleep center",
      "body-female": "Contact your doctor for a referral, or visit our sleep center locator to find a sleep specialist in a location that's convenient for you.",
      "button-female": "Find a center",
      "image-alt-female": "A smiling female doctor holding a tablet"
    },
    "motivational-message-section": {
      "headline": "Why taking action matters",
      "body-single": "You mentioned <motivation></motivation> as a key motivation. Bringing these results to a doctor and talking through what you're experiencing can help you move toward better energy and mental sharpness.",
      "body-multiple": "You mentioned <leading></leading> and <last></last> as key motivations. Bringing these results to a doctor and talking through what you're experiencing can help you move toward better energy and mental sharpness.",
      "body-fallback": "Bringing these results to a doctor and talking through what you're experiencing can help you move toward better energy and mental sharpness.",
      "motivations": {
        "concerns-about-snoring": "concerns about snoring",
        "low-energy": "low energy",
        "existing-health-concerns": "existing health concerns",
        "doctors-recommendation": "a doctor's recommendation",
        "work-performance": "work performance",
        "sudden-event": "a sudden event"
      }
    },
    "book-appointment-section": {
      "take-the-next-step": "Take the next step toward <highlight>better sleep</highlight>",
      "you-dont-have-to": "<bold>You don't have to figure this out alone.</bold> Identifying a sleep condition early can help you take steps to protect your health.<br></br>Based on your results, it may be helpful to talk with a licensed healthcare provider to talk through your symptoms and explore next steps.",
      "book-a-virtual-visit": "Book a virtual visit",
      "check-your-insurance": "Check your insurance coverage",
      "doctor-image-alt": "A doctor with a stethoscope displayed on a smartphone",
      "silenced-symptoms": "Silenced symptoms can deepen <highlight>health risks.</highlight>",
      "for-many-women": "<bold>For many women, poor sleep and feeling tired is seen as normal or \"just a part of aging.\" Because of this, women's sleep struggles are often dismissed, misdiagnosed or overshadowed.</bold><sup>7</sup>",
      "taking-this-assessment": "Based on your results, it may be helpful to talk through what you're experiencing with a licensed healthcare provider, and they can help you move toward better energy and mental sharpness.",
      "low-risk-headline": "What should you do <highlight>next?</highlight>",
      "low-risk-body": "Changes in sleep quality — no matter the cause — can have a real impact on how you feel during the day. You know your body best, and a doctor can help you decide what to do next. Consider talking through your symptoms with a licensed healthcare provider.",
      "image-alt": "A woman sleeping peacefully"
    },
    "download-report-section": {
      "headline-default": "Get your personal sleep insights summary",
      "headline-female": "Get your personal sleep insights summary",
      "body": "Your sleep report summarizes your responses and highlights key insights about your sleep. Sharing it with your doctor can help guide a more informed conversation about what to do next.",
      "secure-label": "PRIVATE AND SECURE",
      "button": "Download report"
    },
    "social-proof-section": {
      "image-alt": "Clara, age 40, 1-kid mom",
      "chip-top": "1-kid mom",
      "chip-bottom": "Age 40",
      "headline": "\"...listen to your body and <gradient>never ignore</gradient> your symptoms.\"",
      "testimonial": "When I think about how long I lived with untreated sleep apnea, I feel lucky; but I also feel like I could have been more responsible with my own health. I wish I had taken my symptoms more seriously and researched earlier. My advice to others who think they may have sleep apnea is to listen to your body and never ignore your symptoms. You’ll be glad for it in the long run.",
      "attribution": "- Clara",
      "disclaimer": "This story is one person's experience and is shared for general informational purposes only. Talk to your healthcare provider for medical advice."
    },
    "redefining-sleep": {
      "cta-copy-link": "Copy link",
      "stats-header": "Nearly 1 million",
      "stats-body": "people worldwide have taken this test",
      "woman": {
        "top-headline": "We're redefining how women's sleep is <bold>understood.</bold>",
        "bottom-headline": "Share these tools with someone you care about.",
        "invite-body": "<bold>Invite her</bold> to learn more about her sleep.",
        "invite-body-mobile": "Share these tools with someone you care about. <bold>Invite her</bold> to learn more about her sleep."
      },
      "generic": {
        "top-headline": "We're helping people across the world better understand their sleep.",
        "bottom-headline": "Share these tools with someone you care about.",
        "invite-body": "<bold>Invite them</bold> to learn more about their sleep.",
        "invite-body-mobile": "Share these tools with someone you care about. <bold>Invite them</bold> to learn more about their sleep."
      },
      "image-alt-large": "A woman resting her head on her hand",
      "image-alt-small": "A woman sitting and relaxing while leaning back"
    },
    "mynight-banner": {
      "headline": "Make night anywhere",
      "description": "Create a darker, more comfortable sleep environment\non your terms with the myNight™ Mask.",
      "button-text": "Get yours today",
      "button-aria-label": "Get the myNight mask - opens in new window",
      "image-alt": "myNight mask product image on wooden dock surface"
    },
    "references-section": {
      "disclaimer-title": "Disclaimer",
      "references-title": "References",
      "expand-references": "Expand references",
      "collapse-references": "Collapse references",
      "disclaimer-text": "This questionnaire should only be used as a general sleep wellness tool that may help you to identify potential indicators for general sleep issues. This questionnaire is intended for use only for general wellbeing purposes or to encourage or maintain a healthy lifestyle, and is not intended to be used for any medical purpose (such as the detection, diagnosis, monitoring, management or treatment of any medical condition or disease). Any health-related information provided by this questionnaire should not be treated as medical advice. Please consult a physician for any medical advice required. Your reliance on information obtained through the use of this questionnaire is solely at your own risk. We strongly recommend consulting with a health care professional about the results of your sleep questionnaire.<br></br><br></br>Conditions related to sleep health, such as snoring, partner snoring, acid reflux, fragmented sleep, or restless legs, are determined based on user-reported responses to questions about snoring, bed partner snoring, light/poor sleep, and the need to move legs while sleeping in order to feel more comfortable.<br></br><br></br>This page may contain AI-generated images.",
      "default": {
        "ref-1": "Source: Boers, et al., (2025). Lancet Respiratory Medicine.",
        "ref-2": "Source: Zhang et al. Sleep Medicine Reviews. 2019;45:1-17.",
        "ref-3": "Source: Maimon, N., & Hanly, P. J. (2010). Journal of clinical sleep medicine, 6(5), 475-478.",
        "ref-4": "Source: Huttunen et al. (2021). Sleep, 44(10), zsab142.",
        "ref-5": "Source: Li, D., Kuang, B., Lobbezoo et al. (2023). Journal of Clinical Sleep Medicine, 19(3), 443-451.",
        "ref-6": "Source: Srivali et al. J Clin Neurosci. 2025 Mar;133:111075."
      },
      "female": {
        "ref-1": "Source: Wimms et al. Sleep, 46, A234.",
        "ref-2": "Source: Zhang et al. Sleep Medicine Reviews. 2019;45:1-17.",
        "ref-3": "Source: Maimon et al. (2010). Journal of clinical sleep medicine, 6(5), 475-478.",
        "ref-4": "Source: Huttunen et al. Sleep, 44(10), zsab142.",
        "ref-5": "Source: Li et al. (2023). Journal of Clinical Sleep Medicine, 19(3), 443-451.",
        "ref-6": "Source: Srivali et al. J Clin Neurosci. 2025 Mar;133:111075.",
        "ref-7": "Source: Kapur et al. J Clin Sleep Med. 2017 Mar 15;13(3):479-504. DOI: 10.5664/jcsm.6506."
      }
    },
    "symptom-results-section": {
      "heading": "{name}, here are your results",
      "subtitle": "Your answers suggest your sleep may be interrupted during the night. Let's walk through what that means.",
      "you-are-at-risk": "POSSIBLE RISK FACTORS FOR",
      "osa": {
        "risk-label-high": "POSSIBLE RISK FACTORS FOR",
        "risk-label-low": "LOW RISK FACTORS FOR",
        "title": "Obstructive sleep apnea (OSA)",
        "description-intro": "This result is based on the answers you provided, including:",
        "cta-label": "What is sleep apnea?",
        "treatment-options-label": "Treatment Options",
        "default": {
          "expanded-description": "Obstructive sleep apnea (OSA) is a condition where breathing stops and restarts throughout the night, making it harder for your body to get oxygen. Sleep apnea can cause daytime  exhaustion, frequent headaches, or make you feel irritable, foggy, and unfocused. During the night, symptoms may include gasping for air while you sleep or snoring so loudly that it keeps your loved ones awake.<br></br><br></br>Over 1 billion people are living with obstructive sleep apnea across the world.<sup>1</sup>"
        },
        "female": {
          "expanded-description": "Obstructive sleep apnea (OSA) is a condition where breathing stops and restarts throughout the night, making it harder for your body to get oxygen. Many women with OSA experience symptoms like daytime tiredness, difficulty staying or falling asleep, nightmares, muscle pain, headaches, anxiety and depression.<br></br><br></br>An estimated 202 million women globally have mild obstructive sleep apnea.<sup>1</sup>"
        }
      },
      "explainability": {
        "how-loud-you-snore": "How loud you snore",
        "your-breathing-patterns-while-sleeping": "Your breathing patterns while sleeping",
        "your-blood-pressure": "Your blood pressure",
        "your-birth-year": "Your birth year",
        "your-height-and-weight": "Your height and weight",
        "your-nap-frequency": "Your nap frequency",
        "symptoms-you-wake-up-with": "Symptoms you wake up with",
        "how-you-described-your-sleep-quality": "How you described your sleep quality",
        "your-difficulty-in-sleeping": "Your difficulty in sleeping",
        "waking-up-with-aching-jaw-or-being-told-you-grind-your-teeth": "Waking up with aching jaw or being told you grind your teeth",
        "answering-yes-to-snoring": "Answering yes to snoring",
        "needing-to-move-your-legs-to-be-comfortable": "Needing to move your legs to be comfortable",
        "your-menopausal-status": "Your menopausal status",
        "waking-up-gasping-for-breath": "Your answer to waking up gasping for breath"
      },
      "insomnia": {
        "title": "Insomnia",
        "description-intro": "This result is based on the answers you provided, including:",
        "bullet-difficulty-staying-asleep": "<li>Difficulty staying asleep</li>",
        "bullet-daytime-sleepiness": "<li>Persistent daytime sleepiness that's lasted over three months</li>",
        "cta-label": "What is insomnia?",
        "expanded-description": "Insomnia is a sleep condition that makes it difficult to fall or stay asleep. It often leads to symptoms like low energy, daytime tiredness and trouble concentrating.<br></br><br></br>35% of people living with insomnia also have sleep apnea.<sup>2</sup>"
      },
      "snoring": {
        "title": "Snoring",
        "description-intro": "This result is based on the answers you provided, including:",
        "bullet-told-you-snore": "<li>You have been told that you snore when you sleep</li>",
        "cta-label": "What is snoring?",
        "expanded-description": "Snoring is a common nighttime occurrence that happens while you sleep. In some cases, snoring can be harmless. In other instances, it may be a sign of a more serious medical condition, making it even more important to understand the cause behind your snoring.<br></br><br></br>Snoring is a common symptom of sleep apnea, and snoring intensity may increase as sleep apnea becomes more severe.<sup>3</sup>"
      },
      "fragmented-sleep": {
        "title": "Fragmented Sleep",
        "description-intro": "This result is based on the answers you provided, including:",
        "bullet-difficulty-staying-asleep": "<li>You have difficulty staying asleep</li>",
        "bullet-waking-during-night": "<li>You often wake up during the night</li>",
        "cta-label": "What is fragmented sleep?",
        "expanded-description": "Fragmented sleep causes a person to wake up frequently throughout the night, disrupting their sleep cycle. It may lead to feeling unrefreshed or tired the next day.<br></br><br></br>People with more severe obstructive sleep apnea may experience more fragmented sleep.<sup>4</sup>"
      },
      "bruxism": {
        "title": "Bruxism",
        "description-intro": "This result is based on the answers you provided, including:",
        "bullet-aching-jaw": "<li>You wake up with an aching jaw</li>",
        "bullet-teeth-grinding": "<li>You've been told that you grind your teeth during sleep</li>",
        "cta-label": "What is bruxism?",
        "expanded-description": "Bruxism is a condition where a person grinds, clenches or gnashes their teeth, usually during sleep. It can lead to jaw pain, headaches and disrupted sleep.<br></br><br></br>One study found that about 50% of adults with obstructive sleep apnea also had sleep bruxism.<sup>5</sup>"
      },
      "restless-legs": {
        "title": "Restless legs syndrome",
        "description-intro": "This result is based on the answers you provided, including:",
        "bullet-urge-to-move-legs": "<li>You feel that you have to move your legs to make them comfortable</li>",
        "cta-label": "What is restless legs?",
        "expanded-description": "Restless legs syndrome is a condition that causes uncomfortable leg sensations. It usually occurs during rest or at night — disrupting your sleep.<br></br><br></br>One study found that 36% of people with obstructive sleep apnea also had restless legs syndrome (RLS).<sup>6</sup>"
      }
    },
    "treatment-cards": {
      "heading": "How sleep apnea can be <managed>managed</managed>",
      "subtitle": "Treatment looks different for everyone. From therapy options to lifestyle changes, there are proven ways to sleep better and feel more rested.",
      "get-started": "Get started",
      "learn-more": "Learn more",
      "talk-to-a-clinician": "Talk to a clinician",
      "pap-therapy": "PAP therapy",
      "positional-therapy": "Positional therapy",
      "oral-appliances": "Oral appliances",
      "nmes": "NMEs",
      "surgery": "Surgery",
      "glp1s": "GLP-1s",
      "coaching": "Coaching",
      "positive-airway-pressure-therapy": "Positive Airway Pressure (PAP) therapy delivers a steady stream of pressurized air through a mask to keep your upper airway open while you sleep, preventing pauses in breathing.<sup>3</sup>",
      "positional-therapy-is-a-non-invasive": "Positional therapy is a non-invasive treatment that may prevent upper airway obstructions by encouraging you to sleep on your side rather than your back, where apnea events are often more severe.<sup>3</sup>",
      "an-oral-appliance-moves-your-jaw": "An oral appliance moves your lower jaw forward to help keep your upper airway open during sleep, which can help reduce breathing interruptions.<sup>3</sup>",
      "neuromuscular-electrical-stimulations": "Neuromuscular electrical stimulations (NMEs) apply gentle electrical pulses while you're awake to strengthen the tongue muscles that help maintain an open upper airway during sleep.<sup>1</sup>",
      "surgical-treatments-for-sleep-apnea": "Surgical treatments for sleep apnea may remove or reposition tissue in the throat or address structural issues that block airflow. Depending on the procedure, surgery may help reduce upper airway blockage during sleep.<sup>2</sup>",
      "glp1-medications-help-reduce-appetite": "GLP-1 medications help reduce appetite and support weight loss. For people whose sleep apnea is related to excess weight, losing weight may reduce upper-airway collapse and lessen breathing interruptions during sleep.<sup>1</sup>",
      "professional-coaches-can-provide": "Healthcare providers can provide personalized guidance and support on your sleep apnea journey, helping you set and reach therapy or lifestyle goals that work for you.",
      "pap-therapy-image-alt": "Positive airway pressure therapy equipment",
      "positional-therapy-image-alt": "Positional therapy for sleep apnea treatment",
      "oral-appliances-image-alt": "Oral appliance device for sleep apnea treatment",
      "nmes-image-alt": "Neuromuscular electrical stimulation therapy device",
      "surgery-image-alt": "Surgical treatment options for sleep apnea",
      "glp1s-image-alt": "GLP-1 medication for metabolic health and sleep apnea",
      "coaching-image-alt": "Professional coaching session for sleep apnea support",
      "references-title": "References",
      "expand-references": "Expand references",
      "collapse-references": "Collapse references",
      "ref-1": "Source: Dobrosielski DA, Papandreou C, Patil SP, Salas-Salvadó J. Diet and exercise in the management of obstructive sleep apnoea and cardiovascular disease risk. Eur Respir Rev. 2017;26(144):160110.",
      "ref-1-url": "https://pubmed.ncbi.nlm.nih.gov/28659501/",
      "ref-2": "Source: Srijithesh PR, Aghoram R, Goel A, Dhanya J. Positional therapy for obstructive sleep apnoea. Cochrane Database Syst Rev. 2019;(5):CD010990.",
      "ref-2-url": "https://pmc.ncbi.nlm.nih.gov/articles/PMC6491901/",
      "ref-3": "Source: Vega ME, Diaz-Abad M, Jaffe F, et al. Use of positional therapy when incorporated into a diagnosis-treatment algorithm for obstructive sleep apnea. Sleep Sci. 2019;12(3):15-20.",
      "ref-3-url": "https://pmc.ncbi.nlm.nih.gov/articles/PMC6508942/"
    }
  }
}
